import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoginResponse, FiscalYearAccess } from '../../types/auth.types';
import { clearCache } from '../../services/apiCache';
import { getActiveFiscalYearId, setActiveFiscalYearId, clearActiveFiscalYearId } from '../../services/fiscalYearStorage';

// ✅ دالة فك الـ Token واستخراج الصلاحيات
const decodeToken = (token: string): string[] => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    
    // استخراج الصلاحيات من claim "Permission"
    if (Array.isArray(payload.Permission)) {
      return payload.Permission;
    } else if (payload.Permission) {
      return [payload.Permission];
    }
    return [];
  } catch (e) {
    console.error('Error decoding token:', e);
    return [];
  }
};

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: {
    userId: number;
    userName: string;
    fullName: string;
    roles: string[];
    isSuperAdmin: boolean;
    branchName: string | null;
  } | null;
  permissions: string[];  // ✅ إضافة الصلاحيات
  availableFiscalYears: FiscalYearAccess[];
  currentFiscalYearId: number | null;
  selectedFiscalYearId: number | null;
}

// ✅ استخراج الصلاحيات من Token المحفوظ
const savedToken = localStorage.getItem('token');
const savedPermissions = savedToken ? decodeToken(savedToken) : [];

const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem('token'),
  token: localStorage.getItem('token'),
  user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null,
  permissions: savedPermissions,  // ✅ تحميل الصلاحيات
  availableFiscalYears: localStorage.getItem('fiscalYears') ? JSON.parse(localStorage.getItem('fiscalYears')!) : [],
  currentFiscalYearId: localStorage.getItem('currentFiscalYearId') ? Number(localStorage.getItem('currentFiscalYearId')) : null,
  // per-tab selected year (see services/fiscalYearStorage.ts)
  selectedFiscalYearId: getActiveFiscalYearId() ? Number(getActiveFiscalYearId()) : null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = {
        userId: action.payload.userId,
        userName: action.payload.userName,
        fullName: action.payload.fullName,
        roles: action.payload.roles,
        isSuperAdmin: action.payload.isSuperAdmin,
        branchName: action.payload.branchName || null,
      };
      
      // ✅ استخراج الصلاحيات من Token
      state.permissions = decodeToken(action.payload.token);
      
      state.availableFiscalYears = action.payload.availableFiscalYears;
      state.currentFiscalYearId = action.payload.currentFiscalYearId;
      state.selectedFiscalYearId = action.payload.currentFiscalYearId;

      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(state.user));
      localStorage.setItem('fiscalYears', JSON.stringify(action.payload.availableFiscalYears));
      if (action.payload.currentFiscalYearId) {
        localStorage.setItem('currentFiscalYearId', action.payload.currentFiscalYearId.toString());
        setActiveFiscalYearId(action.payload.currentFiscalYearId);
      } else {
        clearActiveFiscalYearId();   // no current year — never keep a stale year from a previous session
      }
    },

    setSelectedFiscalYear: (state, action: PayloadAction<number>) => {
      state.selectedFiscalYearId = action.payload;
      setActiveFiscalYearId(action.payload);
      clearCache();
    },

    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.permissions = [];  // ✅ مسح الصلاحيات
      state.availableFiscalYears = [];
      state.currentFiscalYearId = null;
      state.selectedFiscalYearId = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('fiscalYears');
      localStorage.removeItem('currentFiscalYearId');
      clearActiveFiscalYearId();
    },
  },
});

export const { setCredentials, setSelectedFiscalYear, logout } = authSlice.actions;
export default authSlice.reducer;